// _shared/hooks/useGallery.js
import { useState, useEffect, useRef } from 'react';
import { galleryService } from '../../_services/galleryService';

export const useGallery = (slug, initialImages = [], isEditable = false) => {
  const [images, setImages] = useState(initialImages);
  const [previewImages, setPreviewImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchGallery = async () => {
      if (!slug) return;
      if (hasFetched.current) return;
      if (initialImages.length > 0) {
        setImages(initialImages);
        hasFetched.current = true;
        return;
      }

      try {
        const galleryData = await galleryService.getGallery(slug);
        setImages(galleryData);
        hasFetched.current = true;
      } catch (error) {
        console.error("Error al cargar la galería:", error);
        setError('Error al cargar la galería');
      }
    };

    fetchGallery();
  }, [slug, initialImages]);

  /**
   * Manejar cambio de archivos
   */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validation = galleryService.validateFiles(files);
    
    setSelectedFiles(validation.valid);
    setError(validation.error || '');

    // Limpiar previews anteriores
    galleryService.revokePreviews(previewImages);

    // Crear nuevos previews
    if (validation.valid.length > 0) {
      const newPreviews = galleryService.createPreviews(validation.valid);
      setPreviewImages(newPreviews);
    } else {
      setPreviewImages([]);
    }
  };

  /**
   * Limpiar previews al desmontar
   */
  useEffect(() => {
    return () => {
      galleryService.revokePreviews(previewImages);
    };
  }, [previewImages]);

  /**
   * Subir imágenes
   */
  const handleUpload = async () => {
    if (!slug || selectedFiles.length === 0) {
      setError('No hay imágenes para subir');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const response = await galleryService.uploadImages(slug, selectedFiles);
      
      setImages((prev) => [...prev, ...response.updatedGallery]);
      setPreviewImages([]);
      setSelectedFiles([]);
      
      return response.updatedGallery;
    } catch (error) {
      setError(error.response?.data?.message || 'Error al subir imágenes');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Alternar modo edición
   */
  const handleEditToggle = () => {
    if (isEditable) {
      setIsEditing(!isEditing);
    }
  };

  /**
   * Cancelar edición (limpiar selección)
   */
  const cancelEdit = () => {
    galleryService.revokePreviews(previewImages);
    setPreviewImages([]);
    setSelectedFiles([]);
    setError('');
    setIsEditing(false);
  };

  return {
    // Estado
    images,
    previewImages,
    selectedFiles,
    isSaving,
    isEditing,
    error,

    // Setters
    setImages,
    setPreviewImages,
    setSelectedFiles,
    setError,

    // Handlers
    handleFileChange,
    handleUpload,
    handleEditToggle,
    cancelEdit,
  };
};